import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useIsMobileQuery } from 'src/hooks/useMediaQuery'
import classNames from 'classnames'
import { Scanner as QrScanner } from '@yudiel/react-qr-scanner'
import { useGlobalContext } from 'src/stores/GlobalContext'
import { useToastAlert } from 'src/stores/ToastContext'
import generalStyles from 'src/assets/styles/generalStyles.module.scss'
import Loader from 'src/components/Elements/Loader'
import AlertModal from 'src/components/Elements/AlertModal'
import usePostBizCardExchange from 'src/queries/attendees/usePostBizCardExchange'
import userStyles from './userStyles.module.scss'
import fontStyles from 'src/assets/styles/fontStyles.module.scss'
import BusinessCardAgreement from './components/BusinessCardAgreement'

const QR_SCANNER_CONSTRAINTS = {
  facingMode: 'environment',
  width: {
    ideal: 1280
  },
  height: {
    ideal: 720
  }
}

const QR_SCANNER_STYLES = {
  container: { 
    width: '100%',
    aspectRatio: 'auto',
    display: 'block',
    overflow: 'hidden'
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  }
}

const QR_SCANNER_COMPONENTS = {
  finder: false
}

const Scanner = () => {
  const isMobileSize = useIsMobileQuery()
  const navigate = useNavigate()
  const location = useLocation()
  const bizCardScannedReturnPath = location.state?.bizCardScannedReturnPath
  const { currentEvent, eventId, currentEventBizCardAccess } =
    useGlobalContext()
  const { setAlert } = useToastAlert()
  const { mutate } = usePostBizCardExchange(bizCardScannedReturnPath)
  const [cameraOn, setCameraOn] = useState(false)
  const [openModal, setOpenModal] = useState(false)

  useEffect(() => {
    if (currentEventBizCardAccess) {
      setCameraOn(true)
    }
  }, [currentEventBizCardAccess])

  useEffect(() => {
    if (!currentEventBizCardAccess) {
      setOpenModal(true)
    }
  }, [eventId])

  const closeModal = useCallback(() => {
    setOpenModal(false)
    navigate(-1)
  }, [])

  const handleScan = useCallback((result) => {
    if (result && result[0]) {
      mutate({ business_card_hash: result[0].rawValue, event_id: currentEvent.id })
    }
  }, [mutate, currentEvent])

  const handleError = useCallback((error) => {
    setAlert({
      status: 500,
      message: error?.message || 'Failed to access camera',
      title: 'Error',
      forceLogout: false
    })
  }, [setAlert])

  return (
    <>
      <div
        className={classNames(
          generalStyles.alignVertical,
          generalStyles.flexColumn,
          {
            marginRight: 0,
            marginLeft: 0
          }
        )}
      >
        <div className={userStyles.cameraContainer}>
          <div className={userStyles.loaderContainer}>
            <Loader />
          </div>
          {cameraOn && (
            <>
              <div className={userStyles.cameraInstructions}>
                <span
                  className={fontStyles.pxsmallboldlsssin01whitegraphik12pt}
                >
                  Only one person needs to scan to exchange Virtual Business
                  Cards
                </span>
              </div>
              <QrScanner
                onScan={handleScan}
                onError={handleError}
                scanDelay={100}
                sound={false}
                constraints={QR_SCANNER_CONSTRAINTS}
                styles={QR_SCANNER_STYLES}
                components={QR_SCANNER_COMPONENTS}
                classNames={{
                  container: classNames(userStyles.camera, {
                    [userStyles.mobile]: isMobileSize
                  })
                }}
              />
            </>
          )}
        </div>
      </div>
      {openModal && (
        <AlertModal
          title="Virtual Business Card Exchange"
          closeModal={closeModal}
        >
          <BusinessCardAgreement closeModal={() => setOpenModal(false)} />
        </AlertModal>
      )}
    </>
  )
}

export default Scanner
